import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Lightning, CalendarDots, GameController, Trophy, Users, Check, Clock,
  Moon, GraduationCap, Sparkle, Play, Pause, ArrowClockwise, Megaphone,
  CaretDown, CaretUp, UserCheck, WarningCircle, Timer, ArrowRight,
  CheckCircle, XCircle, Plus, Minus
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ax = () => axios.create({ baseURL: API_URL, withCredentials: true });

const TEMPLATE_ICONS = {
  'moon': Moon, 'game-controller': GameController, 'graduation-cap': GraduationCap,
  'trophy': Trophy, 'sparkle': Sparkle,
};

// ════════════════════════════════════════
//  QUICK CREATE
// ════════════════════════════════════════

export function ShopQuickCreate() {
  const { shopId } = useParams();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ game_name: '', title: '', max_players: null, hours_from_now: null });
  const [creating, setCreating] = useState(false);
  const [recentEvents, setRecentEvents] = useState([]);

  useEffect(() => {
    ax().get('/api/shop-ops/templates').then(r => setTemplates(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    ax().get(`/api/shop-portal/${shopId}/events?status=upcoming`).then(r => {
      setRecentEvents(r.data.slice(0, 3));
    }).catch(() => {});
  }, [shopId]);

  const create = async () => {
    if (!selected) return;
    setCreating(true);
    try {
      const payload = { template_id: selected.template_id };
      if (form.game_name) payload.game_name = form.game_name;
      if (form.title) payload.title = form.title;
      if (form.max_players) payload.max_players = form.max_players;
      if (form.hours_from_now !== null) payload.hours_from_now = form.hours_from_now;
      const res = await ax().post(`/api/shop-ops/${shopId}/quick-event`, payload);
      toast.success(`Created: ${res.data.title}`);
      setSelected(null);
      setForm({ game_name: '', title: '', max_players: null, hours_from_now: null });
      ax().get(`/api/shop-portal/${shopId}/events?status=upcoming`).then(r => setRecentEvents(r.data.slice(0, 3))).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create event');
    }
    setCreating(false);
  };

  const inputCls = "w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#EAB308]/40 focus:outline-none placeholder:text-[#444]";

  return (
    <div data-testid="shop-quick-create">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Quick Create</h2>
        <p className="text-[13px] text-[#555] mt-1">One-tap event creation for your shop</p>
      </div>

      {/* Template Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {templates.map(t => {
          const Icon = TEMPLATE_ICONS[t.icon] || Lightning;
          const isSelected = selected?.template_id === t.template_id;
          return (
            <motion.button
              key={t.template_id}
              whileTap={{ scale: 0.97 }}
              onClick={() => setSelected(isSelected ? null : t)}
              className={`relative p-4 rounded-xl border text-left transition-all ${
                isSelected
                  ? 'border-[#EAB308]/40 bg-[#EAB308]/[0.06]'
                  : 'border-white/[0.06] bg-[#0e0e16] hover:border-white/[0.12]'
              }`}
              data-testid={`quick-template-${t.template_id}`}
            >
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ backgroundColor: `${t.accent}15` }}>
                <Icon size={20} weight="duotone" style={{ color: t.accent }} />
              </div>
              <p className="text-white font-semibold text-sm">{t.label}</p>
              <p className="text-[11px] text-[#555] mt-1 line-clamp-2">{t.default_description}</p>
              <div className="flex items-center gap-2 mt-2 text-[10px] text-[#444]">
                <span>{t.default_max_players} max</span>
                <span>{t.hours_offset === 0 ? 'Starts now' : `In ${t.hours_offset}h`}</span>
              </div>
              {isSelected && (
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: t.accent }}>
                  <Check size={12} weight="bold" className="text-black" />
                </div>
              )}
            </motion.button>
          );
        })}
      </div>

      {/* Customize Panel */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-5 mb-6">
              <p className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
                <Lightning size={16} weight="duotone" style={{ color: selected.accent }} />
                Customize (optional)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="text-[11px] text-[#555] mb-1 block">Custom Title</label>
                  <input className={inputCls} placeholder={selected.default_title}
                    value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                    data-testid="quick-title-input" />
                </div>
                <div>
                  <label className="text-[11px] text-[#555] mb-1 block">Game</label>
                  <input className={inputCls} placeholder="Any game"
                    value={form.game_name} onChange={e => setForm({ ...form, game_name: e.target.value })}
                    data-testid="quick-game-input" />
                </div>
                <div>
                  <label className="text-[11px] text-[#555] mb-1 block">Max Players</label>
                  <input className={inputCls} type="number" placeholder={String(selected.default_max_players)}
                    value={form.max_players || ''} onChange={e => setForm({ ...form, max_players: parseInt(e.target.value) || null })} />
                </div>
                <div>
                  <label className="text-[11px] text-[#555] mb-1 block">Starts in (hours)</label>
                  <input className={inputCls} type="number" step="0.5" placeholder={String(selected.hours_offset)}
                    value={form.hours_from_now ?? ''} onChange={e => setForm({ ...form, hours_from_now: parseFloat(e.target.value) || null })} />
                </div>
              </div>
              <button
                onClick={create}
                disabled={creating}
                className="w-full py-3 rounded-xl font-semibold text-sm text-black transition-all active:scale-[0.98]"
                style={{ backgroundColor: selected.accent }}
                data-testid="quick-create-submit"
              >
                {creating ? 'Creating...' : `Create ${selected.label}`}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recent Events */}
      {recentEvents.length > 0 && (
        <div>
          <p className="text-sm font-semibold text-[#888] mb-3">Recent Events</p>
          <div className="space-y-2">
            {recentEvents.map(e => (
              <div key={e.id} className="flex items-center gap-3 bg-[#0e0e16] border border-white/[0.06] rounded-lg px-4 py-3">
                <CalendarDots size={16} weight="duotone" className="text-[#9D4CDD] shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{e.title}</p>
                  <p className="text-[11px] text-[#555]">
                    {e.scheduled_start ? new Date(e.scheduled_start).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    {e.game_name ? ` \u00B7 ${e.game_name}` : ''}
                  </p>
                </div>
                <span className="text-[10px] text-[#555]">{e.rsvp_going || 0} RSVPs</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ════════════════════════════════════════
//  RSVP DASHBOARD
// ════════════════════════════════════════

export function ShopRSVPDashboard() {
  const { shopId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedEvent, setExpandedEvent] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    ax().get(`/api/shop-ops/${shopId}/today`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [shopId]);

  useEffect(() => { load(); }, [load]);

  const checkIn = async (eventId, userId) => {
    try {
      await ax().put(`/api/shop-ops/${shopId}/today/${eventId}/check-in/${userId}`);
      toast.success('Checked in!');
      load();
    } catch { toast.error('Check-in failed'); }
  };

  const updateEventStatus = async (eventId, status) => {
    try {
      await ax().put(`/api/shop-ops/${shopId}/today/${eventId}/status`, { status });
      toast.success(`Event ${status}`);
      load();
    } catch { toast.error('Failed'); }
  };

  if (loading && !data) return (
    <div data-testid="shop-rsvp-dashboard">
      <h2 className="text-xl font-bold text-white mb-6" style={{ fontFamily: 'Unbounded' }}>Today's RSVPs</h2>
      <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-32 bg-white/[0.03] rounded-xl animate-pulse" />)}</div>
    </div>
  );

  const summary = data?.summary || {};

  return (
    <div data-testid="shop-rsvp-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Today's RSVPs</h2>
          <p className="text-[13px] text-[#555] mt-1">{data?.date ? new Date(data.date + 'T00:00').toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' }) : ''}</p>
        </div>
        <button onClick={load} className="text-[#555] hover:text-white transition-colors" data-testid="rsvp-refresh-btn">
          <ArrowClockwise size={20} />
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#555] mb-1">Events</p>
          <p className="text-2xl font-bold text-white">{summary.total_events || 0}</p>
        </div>
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#555] mb-1">RSVPs</p>
          <p className="text-2xl font-bold text-[#9D4CDD]">{summary.total_rsvps || 0}</p>
        </div>
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#555] mb-1">Checked In</p>
          <p className="text-2xl font-bold text-[#10B981]">{summary.total_checked_in || 0}</p>
        </div>
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-wider text-[#555] mb-1">Fill Rate</p>
          <p className="text-2xl font-bold text-[#EAB308]">{summary.fill_rate || 0}%</p>
        </div>
      </div>

      {/* Events List */}
      {(!data?.events || data.events.length === 0) && (
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-8 text-center">
          <CalendarDots size={32} weight="duotone" className="text-[#333] mx-auto mb-3" />
          <p className="text-[#555] text-sm">No events scheduled for today</p>
          <p className="text-[#333] text-xs mt-1">Use Quick Create to set one up!</p>
        </div>
      )}

      <div className="space-y-3">
        {data?.events?.map(evt => {
          const isExpanded = expandedEvent === evt.event_id;
          const startTime = evt.scheduled_start ? new Date(evt.scheduled_start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
          const endTime = evt.scheduled_end ? new Date(evt.scheduled_end).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

          return (
            <div key={evt.event_id} className="bg-[#0e0e16] border border-white/[0.06] rounded-xl overflow-hidden" data-testid={`rsvp-event-${evt.event_id}`}>
              {/* Event Header */}
              <button
                onClick={() => setExpandedEvent(isExpanded ? null : evt.event_id)}
                className="w-full p-4 flex items-center gap-3 text-left hover:bg-white/[0.02] transition-colors"
              >
                <div className="shrink-0">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    evt.status === 'active' ? 'bg-[#10B981]/10' : evt.status === 'completed' ? 'bg-[#555]/10' : 'bg-[#9D4CDD]/10'
                  }`}>
                    <span className="text-lg font-bold" style={{ color: evt.status === 'active' ? '#10B981' : evt.status === 'completed' ? '#555' : '#9D4CDD' }}>
                      {evt.going + evt.checked_in}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-white font-semibold text-sm truncate">{evt.title}</p>
                    <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-full font-semibold ${
                      evt.status === 'active' ? 'bg-[#10B981]/10 text-[#10B981]' :
                      evt.status === 'completed' ? 'bg-[#555]/10 text-[#555]' :
                      'bg-[#EAB308]/10 text-[#EAB308]'
                    }`}>{evt.status}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-[#555]">
                    <span><Clock size={11} className="inline" /> {startTime}{endTime ? ` - ${endTime}` : ''}</span>
                    {evt.game_name && <span>{evt.game_name}</span>}
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-3">
                  {/* Fill bar */}
                  <div className="hidden sm:block w-20">
                    <div className="h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{
                        width: `${Math.min(evt.fill_pct, 100)}%`,
                        backgroundColor: evt.fill_pct >= 90 ? '#EF4444' : evt.fill_pct >= 70 ? '#EAB308' : '#10B981',
                      }} />
                    </div>
                    <p className="text-[9px] text-[#444] text-right mt-0.5">{evt.going + evt.checked_in}/{evt.max_players}</p>
                  </div>
                  {isExpanded ? <CaretUp size={16} className="text-[#555]" /> : <CaretDown size={16} className="text-[#555]" />}
                </div>
              </button>

              {/* Expanded Attendee List */}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: 'auto' }}
                    exit={{ height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="border-t border-white/[0.04] px-4 py-3">
                      {/* Quick Actions */}
                      <div className="flex gap-2 mb-3">
                        {evt.status === 'upcoming' && (
                          <button onClick={() => updateEventStatus(evt.event_id, 'active')}
                            className="text-xs px-3 py-1.5 rounded-lg bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20 transition-colors font-medium"
                            data-testid={`event-start-${evt.event_id}`}>
                            <Play size={12} className="inline mr-1" /> Start Event
                          </button>
                        )}
                        {evt.status === 'active' && (
                          <button onClick={() => updateEventStatus(evt.event_id, 'completed')}
                            className="text-xs px-3 py-1.5 rounded-lg bg-[#555]/10 text-[#888] hover:bg-[#555]/20 transition-colors font-medium"
                            data-testid={`event-complete-${evt.event_id}`}>
                            <CheckCircle size={12} className="inline mr-1" /> Complete
                          </button>
                        )}
                      </div>

                      {/* Attendee counts */}
                      <div className="flex gap-4 mb-3 text-[11px]">
                        <span className="text-[#10B981]"><UserCheck size={12} className="inline" /> {evt.checked_in} checked in</span>
                        <span className="text-[#9D4CDD]"><Users size={12} className="inline" /> {evt.going} going</span>
                        {evt.interested > 0 && <span className="text-[#555]">{evt.interested} interested</span>}
                        {evt.waitlisted > 0 && <span className="text-[#EAB308]"><WarningCircle size={12} className="inline" /> {evt.waitlisted} waitlisted</span>}
                      </div>

                      {/* Attendee list */}
                      <div className="space-y-1">
                        {evt.attendees?.map(a => (
                          <div key={a.user_id} className="flex items-center justify-between py-2 px-2 rounded-lg hover:bg-white/[0.02]" data-testid={`attendee-${a.user_id}`}>
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-semibold"
                                style={{
                                  backgroundColor: a.status === 'checked_in' ? 'rgba(16,185,129,0.1)' : 'rgba(255,255,255,0.04)',
                                  color: a.status === 'checked_in' ? '#10B981' : '#888',
                                }}>
                                {(a.name || '?')[0]}
                              </div>
                              <div>
                                <p className="text-sm text-white">{a.name}</p>
                                <p className="text-[10px] text-[#444]">Lv.{a.level} {a.play_style}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {a.status === 'checked_in' ? (
                                <span className="text-[10px] text-[#10B981] bg-[#10B981]/10 px-2 py-0.5 rounded-full font-medium">Checked In</span>
                              ) : a.status === 'going' ? (
                                <button
                                  onClick={() => checkIn(evt.event_id, a.user_id)}
                                  className="text-[11px] px-2.5 py-1 rounded-lg bg-[#10B981]/10 text-[#10B981] hover:bg-[#10B981]/20 transition-colors font-medium"
                                  data-testid={`checkin-${a.user_id}`}
                                >
                                  <Check size={11} className="inline mr-0.5" /> Check In
                                </button>
                              ) : (
                                <span className="text-[10px] text-[#444] bg-white/[0.04] px-2 py-0.5 rounded-full">{a.status}</span>
                              )}
                            </div>
                          </div>
                        ))}
                        {(!evt.attendees || evt.attendees.length === 0) && (
                          <p className="text-[#444] text-xs py-4 text-center">No attendees yet</p>
                        )}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {/* Today's Tournaments */}
      {data?.tournaments?.length > 0 && (
        <div className="mt-8">
          <p className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
            <Trophy size={16} weight="duotone" className="text-[#F59E0B]" /> Today's Tournaments
          </p>
          <div className="space-y-2">
            {data.tournaments.map(t => (
              <TournamentQuickCard key={t.tournament_id} tournament={t} shopId={shopId} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TournamentQuickCard({ tournament: t, shopId }) {
  const navigate = useNavigate();
  const statusColors = { registration: '#00F0FF', in_progress: '#F59E0B', completed: '#10B981', cancelled: '#EF4444' };
  return (
    <button
      onClick={() => navigate(`/shop/${shopId}/tournament-ops/${t.tournament_id}`)}
      className="w-full flex items-center gap-3 bg-[#0e0e16] border border-white/[0.06] rounded-lg px-4 py-3 text-left hover:border-white/[0.12] transition-colors"
      data-testid={`tournament-quick-${t.tournament_id}`}
    >
      <Trophy size={18} weight="duotone" className="text-[#F59E0B] shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white font-medium truncate">{t.name}</p>
        <p className="text-[11px] text-[#555]">{t.game_type} | {t.time} | {t.current_players}/{t.max_players} players</p>
      </div>
      <span className="text-[9px] uppercase px-2 py-0.5 rounded-full font-semibold"
        style={{ backgroundColor: `${statusColors[t.status] || '#666'}15`, color: statusColors[t.status] || '#666' }}>
        {t.status?.replace('_', ' ')}
      </span>
      <ArrowRight size={14} className="text-[#333]" />
    </button>
  );
}


// ════════════════════════════════════════
//  TOURNAMENT DAY OPS
// ════════════════════════════════════════

export function ShopTournamentOps() {
  const { shopId, tournamentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [announceText, setAnnounceText] = useState('');
  const [sending, setSending] = useState(false);
  const [timerForm, setTimerForm] = useState({ duration_minutes: 30, round_name: 'Round' });

  const load = useCallback(() => {
    if (!tournamentId) return;
    setLoading(true);
    ax().get(`/api/shop-ops/${shopId}/tournament-ops/${tournamentId}`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [shopId, tournamentId]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (status) => {
    try {
      await ax().put(`/api/shop-ops/${shopId}/tournament-ops/${tournamentId}/status`, { status });
      toast.success(`Tournament ${status.replace('_', ' ')}`);
      load();
    } catch { toast.error('Failed'); }
  };

  const announce = async () => {
    if (!announceText.trim()) return;
    setSending(true);
    try {
      await ax().post(`/api/shop-ops/${shopId}/tournament-ops/${tournamentId}/announce`, { message: announceText });
      toast.success('Announcement sent to all participants');
      setAnnounceText('');
      load();
    } catch { toast.error('Failed'); }
    setSending(false);
  };

  const timerAction = async (action) => {
    try {
      const payload = { action };
      if (action === 'start') {
        payload.duration_minutes = timerForm.duration_minutes;
        payload.round_name = timerForm.round_name;
      }
      await ax().put(`/api/shop-ops/${shopId}/tournament-ops/${tournamentId}/timer`, payload);
      toast.success(action === 'start' ? 'Timer started' : action === 'pause' ? 'Timer paused' : action === 'resume' ? 'Timer resumed' : 'Timer reset');
      load();
    } catch { toast.error('Failed'); }
  };

  if (!tournamentId) {
    return (
      <div data-testid="shop-tournament-ops" className="text-center py-12">
        <Trophy size={48} weight="duotone" className="text-[#333] mx-auto mb-4" />
        <p className="text-[#555] text-sm">Select a tournament from Today's RSVPs to manage day operations</p>
        <button onClick={() => navigate(`/shop/${shopId}/rsvp-dashboard`)}
          className="mt-4 text-[#EAB308] text-sm hover:underline">
          Go to RSVP Dashboard
        </button>
      </div>
    );
  }

  if (loading && !data) return (
    <div data-testid="shop-tournament-ops">
      <div className="h-48 bg-white/[0.03] rounded-xl animate-pulse mb-4" />
      <div className="h-32 bg-white/[0.03] rounded-xl animate-pulse" />
    </div>
  );

  const statusColors = { registration: '#00F0FF', in_progress: '#F59E0B', completed: '#10B981', cancelled: '#EF4444' };
  const inputCls = "w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#F59E0B]/40 focus:outline-none placeholder:text-[#444]";

  return (
    <div data-testid="shop-tournament-ops">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>{data?.name || 'Tournament'}</h2>
          <div className="flex items-center gap-3 mt-1 text-[13px] text-[#555]">
            <span>{data?.game_type}</span>
            <span>{data?.current_players}/{data?.max_players} players</span>
            <span className="text-[10px] uppercase px-2 py-0.5 rounded-full font-semibold"
              style={{ backgroundColor: `${statusColors[data?.status] || '#666'}15`, color: statusColors[data?.status] || '#666' }}>
              {data?.status?.replace('_', ' ')}
            </span>
          </div>
        </div>
        <button onClick={load} className="text-[#555] hover:text-white transition-colors">
          <ArrowClockwise size={20} />
        </button>
      </div>

      {/* Status Actions */}
      <div className="flex flex-wrap gap-2 mb-6">
        {data?.status === 'registration' && (
          <button onClick={() => updateStatus('in_progress')}
            className="px-4 py-2 rounded-lg bg-[#F59E0B] text-black font-semibold text-sm active:scale-95 transition-transform"
            data-testid="tournament-start-btn">
            <Play size={14} className="inline mr-1" /> Start Tournament
          </button>
        )}
        {data?.status === 'in_progress' && (
          <button onClick={() => updateStatus('completed')}
            className="px-4 py-2 rounded-lg bg-[#10B981] text-white font-semibold text-sm active:scale-95 transition-transform"
            data-testid="tournament-complete-btn">
            <CheckCircle size={14} className="inline mr-1" /> Complete
          </button>
        )}
        {data?.status !== 'cancelled' && data?.status !== 'completed' && (
          <button onClick={() => updateStatus('cancelled')}
            className="px-4 py-2 rounded-lg bg-[#EF4444]/10 text-[#EF4444] font-semibold text-sm active:scale-95 transition-transform">
            <XCircle size={14} className="inline mr-1" /> Cancel
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Round Timer */}
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-5">
          <p className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <Timer size={16} weight="duotone" className="text-[#F59E0B]" /> Round Timer
          </p>
          <TimerDisplay timer={data?.timer} onAction={timerAction} />
          {(!data?.timer || data?.timer?.status !== 'running') && (
            <div className="mt-4 space-y-2">
              <input className={inputCls} placeholder="Round name" value={timerForm.round_name}
                onChange={e => setTimerForm({ ...timerForm, round_name: e.target.value })} />
              <div className="flex items-center gap-2">
                <input className={inputCls} type="number" placeholder="Minutes" value={timerForm.duration_minutes}
                  onChange={e => setTimerForm({ ...timerForm, duration_minutes: parseInt(e.target.value) || 30 })} />
                <button onClick={() => timerAction('start')}
                  className="px-4 py-2.5 rounded-lg bg-[#F59E0B] text-black font-semibold text-sm whitespace-nowrap"
                  data-testid="timer-start-btn">
                  <Play size={14} className="inline mr-1" /> Start
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Announcements */}
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-5">
          <p className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <Megaphone size={16} weight="duotone" className="text-[#EC4899]" /> Announcements
          </p>
          <div className="flex gap-2 mb-4">
            <input className={inputCls} placeholder="Type announcement..."
              value={announceText} onChange={e => setAnnounceText(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && announce()}
              data-testid="announce-input" />
            <button onClick={announce} disabled={sending || !announceText.trim()}
              className="px-4 py-2.5 rounded-lg bg-[#EC4899] text-white font-semibold text-sm whitespace-nowrap disabled:opacity-40"
              data-testid="announce-send-btn">
              Send
            </button>
          </div>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {data?.announcements?.map(a => (
              <div key={a.id} className="bg-white/[0.03] rounded-lg px-3 py-2">
                <p className="text-sm text-white">{a.message}</p>
                <p className="text-[10px] text-[#444] mt-1">
                  {a.announced_by_name} \u00B7 {a.created_at ? new Date(a.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                </p>
              </div>
            ))}
            {(!data?.announcements || data.announcements.length === 0) && (
              <p className="text-[#444] text-xs text-center py-3">No announcements yet</p>
            )}
          </div>
        </div>

        {/* Standings */}
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-5">
          <p className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <Trophy size={16} weight="duotone" className="text-[#EAB308]" /> Live Standings
          </p>
          <div className="space-y-1">
            {data?.standings?.map((p, i) => (
              <div key={p.id} className="flex items-center gap-3 py-2 px-2 rounded-lg hover:bg-white/[0.02]" data-testid={`standing-${i}`}>
                <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-[11px] font-bold ${
                  i === 0 ? 'bg-[#EAB308]/15 text-[#EAB308]' : i === 1 ? 'bg-white/5 text-[#888]' : i === 2 ? 'bg-[#CD7F32]/10 text-[#CD7F32]' : 'bg-white/[0.03] text-[#444]'
                }`}>{i + 1}</span>
                <div className="flex-1">
                  <p className="text-sm text-white">{p.name}</p>
                </div>
                <div className="flex items-center gap-3 text-[11px]">
                  <span className="text-[#10B981]">{p.wins}W</span>
                  <span className="text-[#EF4444]">{p.losses}L</span>
                </div>
              </div>
            ))}
            {(!data?.standings || data.standings.length === 0) && (
              <p className="text-[#444] text-xs text-center py-4">No matches recorded yet</p>
            )}
          </div>
        </div>

        {/* Recent Matches */}
        <div className="bg-[#0e0e16] border border-white/[0.06] rounded-xl p-5">
          <p className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
            <GameController size={16} weight="duotone" className="text-[#9D4CDD]" /> Recent Matches
          </p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {data?.matches?.map(m => (
              <div key={m.id} className="flex items-center gap-2 py-2 px-2 rounded-lg bg-white/[0.02]">
                <Trophy size={14} weight="duotone" className="text-[#EAB308] shrink-0" />
                <p className="text-sm text-white flex-1">{m.winner_name || 'TBD'}</p>
                <p className="text-[10px] text-[#444]">
                  {m.recorded_at ? new Date(m.recorded_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                </p>
              </div>
            ))}
            {(!data?.matches || data.matches.length === 0) && (
              <p className="text-[#444] text-xs text-center py-4">No matches yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Timer Display Component ───

function TimerDisplay({ timer, onAction }) {
  const [remaining, setRemaining] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (!timer) {
      setRemaining(0);
      return;
    }

    if (timer.status === 'running' && timer.ends_at) {
      const calcRemaining = () => {
        const endsAt = new Date(timer.ends_at).getTime();
        const now = Date.now();
        return Math.max(0, Math.floor((endsAt - now) / 1000));
      };
      setRemaining(calcRemaining());
      intervalRef.current = setInterval(() => {
        const r = calcRemaining();
        setRemaining(r);
        if (r <= 0) clearInterval(intervalRef.current);
      }, 1000);
    } else if (timer.status === 'paused') {
      setRemaining(Math.floor(timer.remaining_seconds || 0));
    }

    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [timer]);

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  const isLow = remaining > 0 && remaining <= 60;

  if (!timer || timer.status === 'reset') {
    return <p className="text-[#444] text-sm text-center py-4">No timer running</p>;
  }

  return (
    <div className="text-center">
      <p className="text-[11px] text-[#555] mb-2 uppercase tracking-wider">{timer.round_name || 'Round'}</p>
      <div className={`text-5xl font-mono font-bold tracking-wider transition-colors ${
        remaining <= 0 ? 'text-[#EF4444]' : isLow ? 'text-[#EAB308] animate-pulse' : 'text-white'
      }`}>
        {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
      </div>
      {remaining <= 0 && timer.status === 'running' && (
        <p className="text-[#EF4444] text-sm font-semibold mt-2 animate-pulse">Time's up!</p>
      )}
      <div className="flex gap-2 justify-center mt-4">
        {timer.status === 'running' && (
          <button onClick={() => onAction('pause')}
            className="px-4 py-2 rounded-lg bg-[#EAB308]/10 text-[#EAB308] text-sm font-medium hover:bg-[#EAB308]/20 transition-colors"
            data-testid="timer-pause-btn">
            <Pause size={14} className="inline mr-1" /> Pause
          </button>
        )}
        {timer.status === 'paused' && (
          <button onClick={() => onAction('resume')}
            className="px-4 py-2 rounded-lg bg-[#10B981]/10 text-[#10B981] text-sm font-medium hover:bg-[#10B981]/20 transition-colors"
            data-testid="timer-resume-btn">
            <Play size={14} className="inline mr-1" /> Resume
          </button>
        )}
        <button onClick={() => onAction('reset')}
          className="px-4 py-2 rounded-lg bg-white/5 text-[#888] text-sm font-medium hover:bg-white/10 transition-colors"
          data-testid="timer-reset-btn">
          <ArrowClockwise size={14} className="inline mr-1" /> Reset
        </button>
      </div>
    </div>
  );
}
